import { useTranslation } from 'react-i18next';
import { ReadinessChip } from '../../components/atoms.js';
import type { ChainRef, TestCaseListItem } from '../../lib/catalog-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import { TestCaseDetail } from '../catalog/testcase-detail.js';

// #153 — alles, was nur die Testfall-Ansicht der Zuordnungsseite betrifft (Spaltenköpfe, Zellen,
// Seitenleiste). Aus assignment-page.tsx herausgezogen, damit die Seite bei den Prozess- und
// Kettenlisten bleibt und der Testfall-Modus an einer Stelle liegt.

/** Eine Zeile der rechten Spalte — für alle drei Kanten dieselbe Form. */
export type RightRow = {
  id: string;
  label: string;
  hint: string;
  count: number | null;
  /** Suchfelder des Modus (siehe Seite: Quell-ID zählt auch bei ausgeschaltetem show_source_ref). */
  terms: (string | null)[];
  /** Nur im Testfall-Modus gesetzt — dort rendert die Tabelle ihre eigenen Spalten. */
  tc?: TestCaseListItem;
};

/**
 * Spaltenköpfe des Testfall-Modus. Bei offener Seitenleiste bleiben nur die Spalten, die über die
 * Zuordnung entscheiden — bei 1280 px reicht der Platz sonst nicht für Liste + Leiste.
 */
export function TestCaseHeadCells({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <th className="p-2">{t('assignment.col.testCase')}</th>
      <th className="w-16 p-2 text-right">{t('assignment.col.steps')}</th>
      <th className="p-2">{t('assignment.col.readiness')}</th>
      {!compact && (
        <>
          <th className="p-2">{t('assignment.col.chains')}</th>
          <th className="p-2">{t('assignment.col.source')}</th>
        </>
      )}
    </>
  );
}

/**
 * Zellen einer Testfallzeile (#153). Der Titel ist ein eigener <button> und öffnet die
 * Seitenleiste; das Häkchen links bleibt das einzige Auswahl-Bedienelement — ein Zeilenklick, der
 * zugleich markiert und öffnet, wäre die zweite Bedeutung für dieselbe Geste.
 */
export function TestCaseCells({
  tc,
  active,
  showSourceRef,
  compact,
  onOpen,
}: {
  tc: TestCaseListItem;
  active: boolean;
  showSourceRef: boolean;
  compact: boolean;
  onOpen: () => void;
}) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  // Quell-ID nur, wenn der Projektschalter sie zeigt — derselbe wie im Katalogbaum (#112/A6).
  const idLine = showSourceRef && tc.source_ref ? `${tc.human_id} · ${tc.source_ref}` : tc.human_id;
  // Kettenbezug im Format des Katalogs („DTP_2 · 3/10"). Ohne sichtbare Quell-ID (Schalter aus
  // oder Kette ohne Szenario-ID) steht die TestFlow-ID der Kette — nie eine leere Zeile. Beide
  // Kennungen sind auch Suchbegriffe (assignment-page.tsx).
  const chainLabel = (c: ChainRef) => {
    const ref = showSourceRef && c.source_ref ? c.source_ref : c.human_id;
    return `${ref} · ${c.step}/${c.total}`;
  };
  return (
    <>
      <td className="p-2 align-top text-text-body">
        <button
          type="button"
          onClick={onOpen}
          aria-pressed={active}
          className={`text-left hover:text-brand-accent ${active ? 'text-brand-accent' : ''}`}
        >
          {pickLang(lang, tc.title_de, tc.title_en)}
        </button>
        <div className="font-mono text-[10px] text-text-muted">{idLine}</div>
      </td>
      <td className="p-2 text-right align-top text-xs text-text-body">{tc.step_count}</td>
      <td className="p-2 align-top">
        <ReadinessChip value={tc.readiness} />
      </td>
      {!compact && (
        <>
          <td className="p-2 align-top text-xs text-text-body">
            {tc.chain_refs.length === 0
              ? '–'
              : tc.chain_refs.map((c) => (
                  <div key={c.e2e_id} className="font-mono">
                    {chainLabel(c)}
                  </div>
                ))}
          </td>
          <td className="p-2 align-top text-xs text-text-body">
            {tc.source_filename || tc.source_teilprojekt ? (
              <>
                {tc.source_filename && (
                  <span title={tc.source_filename} className="block max-w-[11rem] truncate">
                    {tc.source_filename}
                  </span>
                )}
                {tc.source_teilprojekt && (
                  <span className="block text-text-muted">{tc.source_teilprojekt}</span>
                )}
              </>
            ) : (
              '–'
            )}
          </td>
        </>
      )}
    </>
  );
}

/**
 * Seitenleiste: die Katalog-Detailansicht, bearbeitbar (catalog.edit). `key` setzt ihren lokalen
 * Zustand (Löschbestätigung, Eingaben) beim Wechsel des Testfalls zurück. Wird der Prozess hier
 * geändert, verschwindet die Zeile aus der Liste (der Testfall gehört jetzt woanders hin) — die
 * Leiste bleibt trotzdem offen und zeigt ihn weiter.
 */
export function TestCaseSidePanel({
  projectId,
  testCaseId,
  onClose,
}: {
  projectId: string;
  testCaseId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <aside
      aria-label={t('assignment.detailPanel')}
      className="flex w-[26rem] shrink-0 flex-col overflow-hidden border-l border-[var(--border)]"
    >
      <div className="flex justify-end border-b border-[var(--border)] px-2 py-1">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('assignment.closeDetail')}
          className="rounded-btn px-2 py-0.5 text-sm text-text-muted hover:text-text-body"
        >
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <TestCaseDetail
          key={testCaseId}
          projectId={projectId}
          testCaseId={testCaseId}
          onDeleted={onClose}
        />
      </div>
    </aside>
  );
}
